import { DatePipe } from '@angular/common';
import { DurationType, RtsaAnniversaryDate } from '@inshuwa-client/models';

export const ICDateFormats = {
  short: 'dd/MM/yyyy',
  medium: 'dd/MM/yyyy HH:mm',
  long: 'dd/MM/yyyy HH:mm:ss',
};

export const getQuarterEndDate = (
  startDate: Date,
  quarter: number,
  useLeadYear = true,
): Date => {
  let daysToAdd = 90 * quarter;
  const start = new Date(startDate);

  if (quarter === 4) {
    if (!useLeadYear) {
      daysToAdd = 365;
    } else {
      const isLeapYear = start.getFullYear() % 4 === 0;
      daysToAdd = isLeapYear ? 366 : 365;
    }
  }
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const startDateInMilliSec = start.getTime();
  const endDateInMilliSec =
    startDateInMilliSec + daysToAdd * millisecondsPerDay;
  return new Date(endDateInMilliSec);
};

export const calculateEndDate = (data: {
  start: Date;
  type: DurationType;
  duration: number;
  useLeadYear?: boolean;
}): Date => {
  switch (data.type) {
    case 'Custom':
      return data.start;
    case 'Days':
      return addDays(data.start, data.duration);
    case 'Months':
      return addMonths(data.start, data.duration);
    case 'Years':
      return addYears(data.start, data.duration);
    case 'Quarters':
      return getQuarterEndDate(data.start, data.duration, data.useLeadYear);
    default:
      return getQuarterEndDate(data.start, data.duration, data.useLeadYear);
  }
};

export const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const daysInYear = (date: Date): number =>
  isLeapYear(date.getFullYear()) ? 366 : 365;

export const durationInDays = (startDate: Date, endDate: Date): number => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const startDateInMilliSec = new Date(startDate).getTime();
  const endDateInMilliSec = new Date(endDate).getTime();
  const diffInMilliSec = endDateInMilliSec - startDateInMilliSec;
  return Math.round(diffInMilliSec / millisecondsPerDay);
};

export const addDays = (date: Date, days: number): Date => {
  date = new Date(date);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const startDateInMilliSec = date.getTime();
  const endDateInMilliSec = startDateInMilliSec + days * millisecondsPerDay;
  return new Date(endDateInMilliSec);
};

export const addMonths = (date: Date, months: number): Date => {
  date = new Date(date);
  const startDate = new Date(date);
  return new Date(startDate.setMonth(startDate.getMonth() + months));
};

export const addYears = (date: Date, years: number): Date => {
  date = new Date(date);
  const startDate = new Date(date);
  return new Date(startDate.setFullYear(startDate.getFullYear() + years));
};

export const parseDate = (date: string): Date => {
  const dateInMiles = Date.parse(date);
  if (isNaN(dateInMiles)) {
    const dateParts = date.split('/');
    return new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
  }
  return new Date(dateInMiles);
};

export const formatDate = (date: Date, format: string): string => {
  const datePipe = new DatePipe('en-US');
  return datePipe.transform(date, format) ?? '';
};

export function getNextQuarter(numberOfQuarters: number, data: Date) {
  const currentQuarter = getQuarter(data);
  let targetQuarter = currentQuarter + numberOfQuarters;

  if (targetQuarter > 4) {
    targetQuarter = targetQuarter % 4 || 4;
  } else {
    targetQuarter = targetQuarter - 1;
  }
  return targetQuarter;
}

export function getAnniversaryEndDate(
  startDate: Date,
  numberOfQuarters: number,
  dates: RtsaAnniversaryDate[],
): Date {
  const currentQuarter = getQuarter(startDate);
  /*
   * The total number of quarters is calculated by adding the current quarter
   * to the number of quarters minus 1 (because the current quarter is already counted).
   * This gives the total number of quarters to be considered.
   */
  const totalQuarters = currentQuarter + numberOfQuarters - 1;

  /*
   * The ending quarter is calculated using the modulo operator to wrap around
   * the quarters. The result is adjusted to ensure it falls within the range of 1 to 4.
   */
  const endingQuarter = ((totalQuarters - 1) % 4) + 1;
  const quarterDate =
    dates.find((item) => item.quarter === endingQuarter)?.endDate ?? new Date();
  const targetDate = new Date(quarterDate);
  if (totalQuarters > 4) {
    targetDate?.setFullYear(targetDate?.getFullYear() + 1);
  }
  return targetDate;
}

export function getQuarter(data: Date): number {
  const month = data.getMonth();
  return Math.floor(month / 3) + 1;
}
